import React, { useEffect, useState } from 'react';
import { LiaCheckCircle } from 'react-icons/lia';
import { FiInfo, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';
import ReservationSteps from '../profileguest/ReservationSteps';

const ModificationSuccessful = () => {
  const [bookingData, setBookingData] = useState(null);
  const [qrData, setQrData] = useState('');
  const [showTaxBreakdown, setShowTaxBreakdown] = useState(false);
  const [numberOfNights, setNumberOfNights] = useState(0);
  const [additionalGuests, setAdditionalGuests] = useState([]);
  const [showAdditionalGuests, setShowAdditionalGuests] = useState(false);
  const [amenitiesData, setAmenitiesData] = useState({
    selectedAmenities: [],
    currentState: {},
    modifications: [],
  });
  const [financialData, setFinancialData] = useState({
    previewData: null,
    refundCalculations: {},
    previewPrices: {},
    isAdjustment: false,
    adjustmentAmount: 0,
    refundAmount: 0,
    amountDueAfterPreview: 0,
    // New fields for combined data
    combined: null,
    projected_total_after_preview: 0,
    amount_due_after_preview: 0,
    current_due_before_preview: 0,
    total_refund: 0,
    total_delta_base: 0,
    total_delta_tax: 0,
    total_service_fee: 0,
    amount_paid: 0,
  });
  const [paymentStatus, setPaymentStatus] = useState({
    isPaid: false,
    amountPaid: 0,
    amountDue: 0,
    unpaidBillingIds: [],
  });

  // Check payment verification from localStorage
  const checkPaymentVerification = () => {
    try {
      const paymentVerification = localStorage.getItem('payment_verification');
      if (paymentVerification) {
        const payment = JSON.parse(paymentVerification);
        if (payment.status === 'success' && payment.verified) {
          return {
            isPaid: true,
            amountPaid: payment.amount || 0,
            paymentId: payment.razorpay_payment_id,
            timestamp: payment.timestamp,
          };
        }
      }
      return { isPaid: false, amountPaid: 0 };
    } catch (error) {
      console.error('Error checking payment verification:', error);
      return { isPaid: false, amountPaid: 0 };
    }
  };

  useEffect(() => {
    const storedBookingData = localStorage.getItem('bookingData');
    if (storedBookingData) {
      try {
        const parsedData = JSON.parse(storedBookingData);
        setBookingData(parsedData);

        const checkInDate = new Date(parsedData.bookingDetails.checkInDate);
        const checkOutDate = new Date(parsedData.bookingDetails.checkOutDate);
        const timeDiff = checkOutDate.getTime() - checkInDate.getTime();
        const nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
        setNumberOfNights(nights);

        const qrContent = JSON.stringify({
          bookingId: parsedData.bookingId,
          orderId: parsedData.orderId,
          guestName: `${parsedData.guestDetails.firstName || ''} ${parsedData.guestDetails.lastName || ''}`,
          checkIn: parsedData.bookingDetails.checkInDate,
          checkOut: parsedData.bookingDetails.checkOutDate,
          roomType: parsedData.bookingDetails.roomType,
          totalDue: parsedData.paymentDetails?.totalDue || 0,
        });
        setQrData(qrContent);

        // Get additional guests from currentState
        if (parsedData.currentState?.additionalGuests) {
          const guests = parsedData.currentState.additionalGuests;
          if (Array.isArray(guests) && guests.length > 0) {
            setAdditionalGuests(guests);
          }
        }

        // Set amenities data from bookingData
        if (parsedData.amenities) {
          setAmenitiesData({
            selectedAmenities: parsedData.amenities.selectedAmenities || [],
            currentState: parsedData.amenities.currentState || {},
            modifications: parsedData.amenities.modifications || [],
          });
        }

        // Check payment verification first
        const paymentVerification = checkPaymentVerification();

        // Extract combined data from apiResponses.previewResponse
        let combinedData = null;
        let projectedTotal = 0;
        let amountDueAfterPreview = 0;
        let currentDueBeforePreview = 0;
        let amountPaid = 0;
        let totalRefund = 0;

        // Check for combined data in apiResponses.previewResponse
        if (parsedData.apiResponses?.previewResponse) {
          const previewResponse = parsedData.apiResponses.previewResponse;
          combinedData = previewResponse.combined;
          projectedTotal = combinedData.projected_total_after_preview || 0;
          amountDueAfterPreview = combinedData.amount_due_after_preview || 0;
          currentDueBeforePreview = previewResponse.current_due_before_preview || 0;
          amountPaid = combinedData.amount_paid || 0;
          totalRefund = combinedData?.total_refund || 0;
        }

        // Also check in financialData
        if (parsedData.financialData) {
          const financial = parsedData.financialData;
          projectedTotal = projectedTotal || financial.projectedTotalAfterPreview || 0;
          amountDueAfterPreview = amountDueAfterPreview || financial.amountDueAfterPreview || 0;
          amountPaid = amountPaid || financial.amountPaid || 0;
          totalRefund = totalRefund || financial.totalRefund || 0;
        }

        // ADJUST AMOUNTS BASED ON PAYMENT VERIFICATION
        if (paymentVerification.isPaid) {
          // If payment is verified as successful, add paid amount to amountPaid
          amountPaid = (amountPaid || 0) + paymentVerification.amountPaid;

          // Reduce amount due by the paid amount
          amountDueAfterPreview = Math.max(
            0,
            amountDueAfterPreview - paymentVerification.amountPaid
          );
        }

        // Set payment status using the extracted data
        setPaymentStatus({
          isPaid: paymentVerification.isPaid || parsedData.paymentDetails?.fullyPaid || false,
          amountPaid: amountPaid,
          amountDue: amountDueAfterPreview,
          unpaidBillingIds: parsedData.paymentDetails?.unpaidBillingIds || [],
        });

        // Update financialData with combined data
        setFinancialData((prev) => ({
          ...prev,
          combined: combinedData,
          projected_total_after_preview: projectedTotal,
          amount_due_after_preview: amountDueAfterPreview,
          current_due_before_preview: currentDueBeforePreview,
          amount_paid: amountPaid,
          total_refund: totalRefund,
          total_delta_base: combinedData?.total_delta_base || 0,
          total_delta_tax: combinedData?.total_delta_tax || 0,
          total_service_fee: combinedData?.total_service_fee || 0,
        }));

        // Set existing financial data from bookingData
        if (parsedData.financialData) {
          setFinancialData((prev) => ({
            ...prev,
            previewData: parsedData.financialData.previewData,
            refundCalculations: parsedData.financialData.refundCalculations || {},
            previewPrices: parsedData.financialData.previewPrices || {},
            isAdjustment: parsedData.financialData.isAdjustment || false,
            adjustmentAmount: parsedData.financialData.adjustmentAmount || 0,
            refundAmount: parsedData.financialData.refundAmount || 0,
            amountDueAfterPreview: parsedData.financialData.amountDueAfterPreview || 0,
          }));
        }

        // Also check for preview data in localStorage
        const previewDataFromStorage = localStorage.getItem('previewData');
        if (previewDataFromStorage) {
          const previewData = JSON.parse(previewDataFromStorage);
          if (previewData) {
            setFinancialData((prev) => ({
              ...prev,
              previewData: previewData,
              isAdjustment:
                previewData?.room_preview?.financial_preview_type === 'recalculation_via_discount',
              adjustmentAmount:
                previewData?.room_preview?.financial_preview_type === 'recalculation_via_discount'
                  ? previewData?.combined?.total_refund || 0
                  : 0,
            }));
          }
        }
      } catch (error) {
        console.error('Error parsing bookingData:', error);
      }
    }
  }, []);

  const handleDone = () => {
    localStorage.clear();
    window.location.href = '/reservation/options';
  };

  const toggleAdditionalGuests = () => {
    setShowAdditionalGuests(!showAdditionalGuests);
  };

  if (!bookingData) {
    return (
      <div className="flex items-center justify-center h-screen bg-white text-black">
        Loading booking details...
      </div>
    );
  }

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Get primary guest info
  const primaryGuest = {
    firstname: bookingData.guestDetails.firstName || '',
    lastname: bookingData.guestDetails.lastName || '',
    emailid: bookingData.guestDetails.email || '',
    phonenumber: bookingData.guestDetails.phoneNumber?.replace('+91', '') || '',
    countrycode: bookingData.guestDetails.phoneNumber?.startsWith('+91') ? '+91' : '',
    clienttype: bookingData.currentState?.clientType || 'Leisure',
  };

  // Get booking details
  const bookingDetails = bookingData.bookingDetails || {};
  const roomType = bookingDetails.roomType || 'N/A';
  // Factors the guest selected for this reservation (stored by ReservationSummary)
  const selectedFactorNames = (bookingData.factors?.selected || [])
    .map((f) => f.factorname || f.factor_name)
    .filter(Boolean);

  // Calculate room charges from financial data
  const getRoomCharges = () => {
    // Use room charges from previewPrices if available
    if (financialData.previewPrices?.roomFinalAmount) {
      return financialData.previewPrices.roomFinalAmount;
    }

    // Calculate from projected total if available
    if (financialData.projected_total_after_preview) {
      const total = financialData.projected_total_after_preview;
      const amenitiesTotal = amenitiesData.currentState.totalAmount || 0;
      const serviceFee =
        financialData.total_service_fee ||
        financialData.previewPrices?.totalServiceFee ||
        financialData.previewPrices?.roomServiceFee ||
        0;
      const taxes = calculateTaxAmounts().totalTax || 0;

      // Calculate room charges: total - amenities - taxes - service fee
      return total - amenitiesTotal - taxes - serviceFee;
    }

    // Fallback calculation
    return paymentStatus.amountDue - (amenitiesData.currentState.totalAmount || 0);
  };

  const roomCharges = getRoomCharges();

  // Calculate tax amounts
  const calculateTaxAmounts = () => {
    const roomTax =
      financialData.previewPrices?.roomTaxAmount ||
      financialData.previewData?.room_preview?.room_details?.new_room_tax_total ||
      0;

    const amenitiesTax = financialData.previewData?.fixed_enhance_services?.tax || 0;

    return {
      roomTax,
      amenitiesTax,
      totalTax: roomTax + amenitiesTax,
    };
  };

  const taxAmounts = calculateTaxAmounts();

  // Check if there's a refund or adjustment
  const hasRefundOrAdjustment =
    financialData.refundAmount > 0 || financialData.adjustmentAmount > 0;
  const isAdjustment = financialData.isAdjustment;
  const refundOrAdjustmentAmount = isAdjustment
    ? financialData.adjustmentAmount
    : financialData.refundAmount;

  // Get modification type
  const modificationType = financialData.previewData?.room_preview?.modification_type || '';
  const isDowngrade = modificationType.includes('downgrade');
  const isUpgrade =
    modificationType.includes('upgrade') || modificationType === 'additional_charge';

  // Calculate service fee
  const serviceFee =
    financialData.total_service_fee ||
    financialData.previewPrices?.totalServiceFee ||
    financialData.previewPrices?.roomServiceFee ||
    financialData.previewData?.room_preview?.service_fee_total_all_modifications ||
    0;

  // Get previous room details for comparison
  const previousRoomTotal = financialData.previewData?.room_preview?.old_total || 0;
  const newRoomTotal = financialData.previewData?.room_preview?.new_total || 0;

  // Calculate grand total
  const grandTotal =
    financialData.projected_total_after_preview ||
    roomCharges + amenitiesData.currentState.totalAmount + serviceFee;

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-2 mt-16 relative">
      <div className="w-full border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <ReservationSteps currentStep={4} />

        {/* Header */}
        <div className="bg-gray-100 text-white p-8 flex justify-between items-start">
          <div>
            <div className="flex items-center mt-4">
              <LiaCheckCircle size={56} className="mr-3 mb-10 text-green-400" />
              <div>
                <h1 className="text-2xl text-black font-bold mt-1">Modification Successful</h1>
                <p className="text-black mt-1">Your reservation has been modified successfully</p>
                <p className="text-lg text-black font-bold tracking-wider pt-4">
                  Reservation Number: {bookingData.orderId}
                </p>
              </div>
            </div>
          </div>
          <div className="ml-auto border border-black p-2 rounded">
            {qrData ? (
              <QRCodeSVG value={qrData} size={112} level="H" includeMargin={true} />
            ) : (
              <div className="w-28 h-28 flex items-center justify-center bg-gray-100">
                <span className="text-xs">Loading QR code...</span>
              </div>
            )}
            <p className="text-xs text-center mt-1 text-black">Scan for details</p>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-3 divide-x divide-gray-200">
          {/* Column 1: Guest Details */}
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-6">Guest Details</h2>

            {/* Primary Guest */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                Primary Guest
              </h3>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    First Name
                  </p>
                  <p className="font-semibold text-gray-900">
                    {primaryGuest.firstname?.charAt(0).toUpperCase() +
                      primaryGuest.firstname?.slice(1).toLowerCase() || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Last Name
                  </p>
                  <p className="font-semibold text-gray-900">
                    {primaryGuest.lastname?.charAt(0).toUpperCase() +
                      primaryGuest.lastname?.slice(1).toLowerCase() || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Phone Number
                  </p>
                  <p className="font-semibold text-gray-900">
                    {primaryGuest.countrycode && primaryGuest.phonenumber
                      ? `${primaryGuest.countrycode} ${primaryGuest.phonenumber}`
                      : bookingData.guestDetails.phoneNumber || 'N/A'}
                  </p>
                </div>
                <div className="">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Profile Type
                  </p>
                  <p className="font-semibold text-gray-900">
                    {primaryGuest.clienttype || 'Leisure'}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Email
                  </p>
                  <p className="font-semibold text-gray-900">
                    {primaryGuest.emailid || bookingData.guestDetails.email || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Guests - Dropdown */}
            <div>
              <div
                className="flex items-center cursor-pointer mb-3"
                onClick={toggleAdditionalGuests}
              >
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mr-2">
                  Additional Guests ({additionalGuests.length})
                </h3>
                {additionalGuests.length > 0 &&
                  (showAdditionalGuests ? <FiChevronUp /> : <FiChevronDown />)}
              </div>

              {showAdditionalGuests && additionalGuests.length > 0 && (
                <div className="space-y-4 mt-3">
                  {additionalGuests.map((guest, index) => (
                    <div key={index} className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-md">
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                          First Name
                        </p>
                        <p className="font-semibold text-gray-900">
                          {guest.firstName?.charAt(0).toUpperCase() +
                            guest.firstName?.slice(1).toLowerCase() || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                          Last Name
                        </p>
                        <p className="font-semibold text-gray-900">
                          {guest.lastName?.charAt(0).toUpperCase() +
                            guest.lastName?.slice(1).toLowerCase() || 'N/A'}
                        </p>
                      </div>
                      {guest.phoneNumber && (
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Phone Number
                          </p>
                          <p className="font-semibold text-gray-900">
                            {guest.countryCode ? `${guest.countryCode} ` : ''}
                            {guest.phoneNumber || 'N/A'}
                          </p>
                        </div>
                      )}
                      {guest.country && (
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Country
                          </p>
                          <p className="font-semibold text-gray-900">
                            {typeof guest.country === 'object'
                              ? guest.country.name
                              : guest.country || 'N/A'}
                          </p>
                        </div>
                      )}
                      {guest.email && guest.email !== 'user@example.com' && (
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                            Email
                          </p>
                          <p className="font-semibold text-gray-900">{guest.email || 'N/A'}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {additionalGuests.length === 0 && (
                <p className="text-sm text-gray-500 italic">No additional guests</p>
              )}
            </div>
          </div>

          {/* Column 2: Stay Details */}
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-6">Stay Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Check-in
                </p>
                <p className="font-semibold text-gray-900">
                  {bookingDetails.checkInDate
                    ? new Date(bookingDetails.checkInDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'N/A'}{' '}
                  (11:00 AM)
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Check-out
                </p>
                <p className="font-semibold text-gray-900">
                  {bookingDetails.checkOutDate
                    ? new Date(bookingDetails.checkOutDate).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })
                    : 'N/A'}{' '}
                  (12:00 PM)
                </p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Room Type
                </p>
                <p className="font-semibold text-gray-900">{roomType}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  Guests
                </p>
                <p className="font-semibold text-gray-900">{bookingDetails.numberOfGuests || 1}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                  No of nights
                </p>
                <p className="font-semibold text-gray-900">{numberOfNights}</p>
              </div>
              {selectedFactorNames.length > 0 && (
                <div className="col-span-2">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Selected Factors
                  </p>
                  <p className="font-semibold text-gray-900">{selectedFactorNames.join(', ')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Column 3: Payment Summary */}
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-2 flex items-center">Payment Summary</h2>
            <div className="space-y-1">
              {/* Room Charges */}
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-sm font-bold text-gray-600">Room Charges - {roomType}</span>
                  <span className="font-semibold text-gray-900">
                    ₹{formatCurrency(Math.max(0, (roomCharges || 0) - (taxAmounts.roomTax || 0)))}
                  </span>
                </div>
              </div>

              {/* Amenities & Services */}
              {amenitiesData.currentState.totalAmount > 0 && (
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm font-bold text-gray-600">Additional Services</span>
                    <span className="font-semibold text-gray-900">
                      ₹{formatCurrency(amenitiesData.currentState.totalAmount)}
                    </span>
                  </div>
                </div>
              )}

              {/* Service Fee */}
              {serviceFee > 0 && (
                <div className="flex justify-between border-t border-gray-200 pt-2">
                  <span className="text-sm font-bold text-gray-600">Modification Service Fee</span>
                  <span className="font-semibold text-gray-900">₹{formatCurrency(serviceFee)}</span>
                </div>
              )}

              {/* Taxes & Fees */}
              <div className="flex justify-between relative">
                <div className="flex items-center">
                  <span className="text-sm font-bold text-gray-600 mr-1">Taxes & Fees</span>
                  <div
                    className="relative"
                    onMouseEnter={() => setShowTaxBreakdown(true)}
                    onMouseLeave={() => setShowTaxBreakdown(false)}
                  >
                    <FiInfo className="text-gray-400 cursor-pointer" size={14} />
                    {showTaxBreakdown && (
                      <div className="absolute left-0 bottom-full mb-2 w-64 bg-white shadow-lg rounded-md p-3 z-10 border border-gray-200">
                        <div className="text-xs text-gray-600">
                          <div className="flex justify-between py-1">
                            <span>Room Tax (5%)</span>
                            <span>₹{formatCurrency(taxAmounts.roomTax)}</span>
                          </div>
                          {taxAmounts.amenitiesTax > 0 && (
                            <div className="flex justify-between py-1">
                              <span>Amenities Tax (5%)</span>
                              <span>₹{formatCurrency(taxAmounts.amenitiesTax)}</span>
                            </div>
                          )}
                          <div className="border-t border-gray-200 mt-1 pt-1 flex justify-between font-medium">
                            <span>Total Tax</span>
                            <span>₹{formatCurrency(taxAmounts.totalTax)}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <span className="font-semibold text-gray-900">
                  ₹{formatCurrency(taxAmounts.totalTax)}
                </span>
              </div>

              {/* Grand Total */}
              <div className="border-t border-gray-200 pt-2">
                <div className="flex justify-between">
                  <span className="font-bold text-gray-900">Total Amount</span>
                  <span className="font-bold text-gray-900">₹{formatCurrency(grandTotal)}</span>
                </div>
              </div>

              {/* Refund/Adjustment Amount */}
              {(financialData.total_refund > 0 || hasRefundOrAdjustment) && (
                <div className="flex justify-between">
                  <span className="font-bold text-gray-900">
                    {isAdjustment ? 'Adjustment Amount' : 'Refund Amount'}
                  </span>
                  <span className="font-bold text-gray-900">
                    ₹{formatCurrency(financialData.total_refund || refundOrAdjustmentAmount)}
                  </span>
                </div>
              )}

              {/* Payment Status - Updated Section */}
              <div className="mt-4 space-y-1">
                {/* Amount Paid with breakdown */}
                <div className="flex justify-between">
                  <div>
                    <span className="font-bold text-gray-900">Amount Paid</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-gray-900">
                      ₹{formatCurrency(paymentStatus.amountPaid)}
                    </span>
                  </div>
                </div>

                {/* Amount Due */}
                {paymentStatus.amountDue > 0 && (
                  <div className="flex justify-between">
                    <span className="font-bold text-gray-900">Amount Due</span>
                    <div className="text-right">
                      <span className="font-bold text-gray-950">
                        ₹{formatCurrency(paymentStatus.amountDue)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Important Notes */}
              <div className="mt-4 bg-gray-50 p-4 rounded-lg">
                <h3 className="text-sm font-semibold mb-2">Important Notes</h3>
                <ul className="text-xs text-gray-600 space-y-1">
                  <li>• Present this confirmation at check-in</li>
                  <li>• Check-in time is 11:00 AM</li>
                  <li>• Check-out time is 12:00 PM</li>
                  <li>• Government approved ID required</li>
                  <li>• Modification service fee may apply for changes</li>
                  {(financialData.total_refund > 0 || refundOrAdjustmentAmount > 0) &&
                    !isAdjustment && <li>• Refunds will be processed within 5-7 business days</li>}
                  {isAdjustment && (
                    <li>• Adjustment amount has been applied to your total balance</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="fixed bottom-4 right-5 z-50">
          <button
            onClick={handleDone}
            className="bg-black text-white px-16 py-3 rounded-md text-sm font-medium hover:bg-gray-800 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-transform duration-200"
          >
            Continue to Check-In
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModificationSuccessful;
